/**
 * QA Assistant Panel
 * AI-powered slide-out chat panel for claim analysis
 * Now with voice support (TTS/STT)
 */
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Send, 
  MessageSquare, 
  Loader2, 
  Sparkles,
  ChevronRight,
  AlertCircle,
  FileText,
  Zap,
  Bot,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Square
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { api, uploadFiles } from '../../../utils/api';
import { formatCurrency } from '../../../utils/financialFormat';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  audioBase64?: string;
  actionButtons?: Array<{
    label: string;
    action: string;
    target?: string;
    query?: string;
    data?: any;  // For recategorization/question data
  }>;
}

interface QAAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
  claimId: string;
  currentStep: number;
  onNavigate?: (target: string) => void;
  anomalyCount?: number;
  reviewCount?: number;
}

export default function QAAssistantPanel({
  isOpen,
  onClose,
  claimId,
  currentStep,
  onNavigate,
  anomalyCount = 0,
  reviewCount = 0,
}: QAAssistantPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState('coral'); // coral = warm/friendly
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [playingMessageIndex, setPlayingMessageIndex] = useState<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // State for Smart Review and Batch Questions
  const [smartReviewLoading, setSmartReviewLoading] = useState(false);
  const [smartReviewResult, setSmartReviewResult] = useState<any>(null);
  const [batchQuestionsLoading, setBatchQuestionsLoading] = useState(false);
  const [batchQuestionsResult, setBatchQuestionsResult] = useState<any>(null);
  const [showSessionInfo, setShowSessionInfo] = useState(false);

  // Generate session ID on mount
  useEffect(() => {
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }, []);

  // Scroll to bottom when new messages arrive — ONLY if the panel is open.
  // Without the isOpen guard, scrollIntoView fires even when the panel is
  // closed (and messages get seeded on mount via loadInitialSuggestions).
  // Because the panel lives inside the main document flow, that scrollIntoView
  // yanks the whole page downward, then the next render snaps it back — the
  // "scrolls down, resets to top, over and over" loop.
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom();
  }, [messages, isOpen]);

  // Stop audio playback when panel closes
  useEffect(() => {
    if (!isOpen) {
      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsPlaying(false);
      }
      // Stop any recording
      if (isRecording && mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        setRecordingDuration(0);
      }
    }
  }, [isOpen]);

  // Load initial suggestions when panel opens
  useEffect(() => {
    if (isOpen && !isInitialized && claimId) {
      loadInitialSuggestions();
    }
  }, [isOpen, claimId, isInitialized]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const loadInitialSuggestions = async () => {
    try {
      setIsLoading(true);
      console.log('Loading suggestions for claim:', claimId, 'step:', currentStep);
      
      const data = await api.post('/api/v1/assistant/suggestions', {
        claim_id: claimId,
        current_step: currentStep,
      }, { showErrorToast: false });

      console.log('Got suggestions:', data);
      setMessages([{
        role: 'assistant',
        content: data.response,
        actionButtons: data.action_buttons,
      }]);
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm your The Figure Co. AI assistant. I can help you understand transactions, anomalies, and calculations. What would you like to know?\n\n*Claim ID: ${claimId}*`,
      }]);
      setIsInitialized(true);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    // Add user message
    const userMessage: Message = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const data = await api.post('/api/v1/assistant/chat', {
        message: text,
        claim_id: claimId,
        current_step: currentStep,
        session_id: sessionId,
      }, { showErrorToast: false });
      
      // Optionally get TTS audio
      let audioBase64: string | undefined;
      if (voiceEnabled) {
        try {
          const ttsData = await api.post('/api/v1/assistant/voice/speak', {
            text: data.response.slice(0, 4096),
            voice: selectedVoice,
            model: 'tts-1',
          }, { showErrorToast: false });
          audioBase64 = ttsData.audio_base64;
        } catch (ttsError) {
          console.error('TTS error:', ttsError);
        }
      }
      
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.response,
        actionButtons: data.action_buttons,
        audioBase64,
        timestamp: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Auto-play audio if enabled
      if (audioBase64 && voiceEnabled) {
        playAudio(audioBase64);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Please try again.",
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Voice recording - click to toggle
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setRecordingDuration(0);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await sendVoiceMessage(audioBlob);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      // Duration timer for UI display
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Microphone access needed -- please check your browser permissions to enable voice input.',
        timestamp: new Date().toISOString(),
      }]);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const sendVoiceMessage = async (audioBlob: Blob) => {
    setIsLoading(true);
    
    try {
      // First transcribe the audio
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      // Use uploadFiles for FormData upload
      const transcribeData = await uploadFiles('/api/v1/assistant/voice/transcribe', formData, { showErrorToast: false });
      const userText = transcribeData.text;
      
      if (!userText || !userText.trim()) {
        throw new Error('No speech detected');
      }
      
      // Add user message with transcription
      setMessages(prev => [...prev, {
        role: 'user',
        content: userText,
        timestamp: new Date().toISOString(),
      }]);
      
      // Now send to chat using api utility
      const chatData = await api.post('/api/v1/assistant/chat', {
        message: userText,
        claim_id: claimId,
        current_step: currentStep,
        session_id: sessionId,
      }, { showErrorToast: false });
      
      // Get TTS for response
      let audioBase64: string | undefined;
      if (voiceEnabled) {
        try {
          const ttsData = await api.post('/api/v1/assistant/voice/speak', {
            text: chatData.response.slice(0, 4096),
            voice: selectedVoice,
            model: 'tts-1',
          }, { showErrorToast: false });
          audioBase64 = ttsData.audio_base64;
        } catch (e) {
          console.error('TTS error:', e);
        }
      }
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: chatData.response,
        actionButtons: chatData.action_buttons,
        audioBase64,
        timestamp: new Date().toISOString(),
      }]);
      
      if (audioBase64 && voiceEnabled) {
        playAudio(audioBase64);
      }
      
    } catch (error: any) {
      console.error('Voice message error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I couldn't process your voice message: ${error.message}`,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const playAudio = (base64Audio: string) => {
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
      audioRef.current = audio;
      setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
      audio.play();
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
    }
  };

  // State for AI actions (recategorization, client questions)
  const [pendingRecategorizations, setPendingRecategorizations] = useState<any[]>([]);
  const [pendingQuestions, setPendingQuestions] = useState<any[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  const handleActionButton = async (button: { action: string; target?: string; query?: string; data?: any }) => {
    if (button.action === 'navigate' && button.target && onNavigate) {
      onNavigate(button.target);
    } else if (button.action === 'query' && button.query) {
      sendMessage(button.query);
    } else if (button.action === 'apply_recategorizations' && button.data) {
      // Apply recategorizations via API
      setActionLoading(true);
      try {
        const result = await api.post('/api/v1/assistant/actions/apply-recategorize', {
          claim_id: claimId,
          transactions: button.data.transactions
        }, { showErrorToast: false });
        if (result.success) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `**Successfully recategorized ${result.updated_count} transactions!**\n\nThe changes have been applied. You may need to refresh the Financial Data view to see the updates.`,
            timestamp: new Date().toISOString()
          }]);
        } else {
          throw new Error(result.detail || 'Failed to apply changes');
        }
      } catch (error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**Error applying recategorizations:** ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease try again or apply the changes manually.`,
          timestamp: new Date().toISOString()
        }]);
      }
      setActionLoading(false);
    } else if (button.action === 'flag_for_client' && button.data) {
      // Flag transactions for client verification
      setActionLoading(true);
      try {
        const result = await api.post('/api/v1/assistant/actions/flag-for-client', {
          claim_id: claimId,
          questions: button.data.questions
        }, { showErrorToast: false });
        if (result.success) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `**Flagged ${result.flagged_count} transactions for client verification!**\n\nThese questions will be included in the next clarification email to the client.\n\nYou can view pending questions in the **Client Clarifications** section.`,
            timestamp: new Date().toISOString()
          }]);
        } else {
          throw new Error(result.detail || 'Failed to flag transactions');
        }
      } catch (error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**Error flagging transactions:** ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease try again or add the questions manually.`,
          timestamp: new Date().toISOString()
        }]);
      }
      setActionLoading(false);
    } else if (button.action === 'preview_recategorizations' && button.data) {
      // Preview the impact of recategorizations
      setActionLoading(true);
      try {
        const result = await api.post('/api/v1/assistant/actions/preview-recategorize', {
          claim_id: claimId,
          transactions: button.data.transactions
        }, { showErrorToast: false });
        if (result.success) {
          const impact = result.impact;
          let impactSummary = `## Preview: Recategorization Impact\n\n`;
          impactSummary += `**${impact.transactions_affected} transactions** will be updated.\n\n`;
          impactSummary += `### Category Changes:\n`;
          for (const [change, data] of Object.entries(impact.category_changes as Record<string, any>)) {
            impactSummary += `- ${change}: ${data.count} items (${formatCurrency(data.total)})\n`;
          }
          impactSummary += `\n### Estimated Claim Impact:\n`;
          impactSummary += `- Extra expenses added: ${formatCurrency(impact.estimated_claim_impact.extra_expenses_added)}\n`;
          impactSummary += `- Net positive impact: ~${formatCurrency(impact.estimated_claim_impact.net_positive_impact_estimate)}\n`;
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: impactSummary,
            timestamp: new Date().toISOString(),
            actionButtons: [
              { label: 'Apply These Changes', action: 'apply_recategorizations', data: button.data },
              { label: 'Cancel', action: 'query', query: 'Okay, I won\'t apply those changes.' }
            ]
          }]);
        }
      } catch (error) {
        console.error('Preview error:', error);
      }
      setActionLoading(false);
    } else if (button.action === 'parse_client_responses' && button.data) {
      // Parse client responses using AI
      setActionLoading(true);
      try {
        const result = await api.post('/api/v1/assistant/actions/parse-client-responses', {
          claim_id: claimId,
          responses: button.data.responses
        }, { showErrorToast: false });
        if (result.parsed_responses && result.parsed_responses.length > 0) {
          let parseSummary = `## AI Analysis of Client Responses\n\n`;
          parseSummary += `${result.summary}\n\n`;
          parseSummary += `### Suggested Categories:\n`;
          for (const parsed of result.parsed_responses) {
            const confidenceLabel = parsed.confidence >= 0.9 ? 'High' : parsed.confidence >= 0.7 ? 'Medium' : 'Low';
            parseSummary += `\n**Transaction ${parsed.transaction_id}:**\n`;
            parseSummary += `- Suggested: **${parsed.suggested_category}**\n`;
            parseSummary += `- Confidence: ${confidenceLabel} (${Math.round(parsed.confidence * 100)}%)\n`;
            parseSummary += `- Reasoning: ${parsed.reasoning}\n`;
            if (parsed.needs_human_review) {
              parseSummary += `- *Needs human review*\n`;
            }
          }
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: parseSummary,
            timestamp: new Date().toISOString(),
            actionButtons: result.parsed_responses.filter((p: any) => p.confidence >= 0.7).map((p: any) => ({
              label: `Apply "${p.suggested_category}" to ${p.transaction_id}`,
              action: 'apply_parsed_category',
              data: { transaction_id: p.transaction_id, category: p.suggested_category }
            }))
          }]);
        } else {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `No client responses found to parse. Make sure you have pending client answers in the clarification queue.`,
            timestamp: new Date().toISOString()
          }]);
        }
      } catch (error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**Error parsing client responses:** ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: new Date().toISOString()
        }]);
      }
      setActionLoading(false);
    } else if (button.action === 'apply_parsed_category' && button.data) {
      // Apply a single parsed category from AI analysis
      setActionLoading(true);
      try {
        const result = await api.post(
          `/api/v1/assistant/actions/apply-parsed-category?claim_id=${claimId}&transaction_id=${button.data.transaction_id}&category=${encodeURIComponent(button.data.category)}&specialist_confirmed=true`,
          null,
          { showErrorToast: false }
        );
        if (result.success) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `**Applied "${button.data.category}" to transaction!**\n\nThe category has been updated. This transaction is now resolved.`,
            timestamp: new Date().toISOString()
          }]);
        } else {
          throw new Error(result.detail || 'Failed to apply category');
        }
      } catch (error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**Error applying category:** ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: new Date().toISOString()
        }]);
      }
      setActionLoading(false);
    } else if (button.action === 'apply_smart_action' && button.data) {
      // Apply a smart review action
      setActionLoading(true);
      try {
        const result = await api.post(`/api/v1/assistant/actions/apply-smart-review-actions?claim_id=${claimId}`, [button.data], { showErrorToast: false });
        if (result.success) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `**Action applied!**\n\n${result.results[0]?.action === 'approve_ai' ? 'AI suggestion approved.' : 
                      result.results[0]?.action === 'recategorize' ? 'Transaction recategorized.' :
                      result.results[0]?.action === 'dismiss' ? 'Anomaly dismissed.' : 'Action completed.'}`,
            timestamp: new Date().toISOString()
          }]);
        } else {
          throw new Error('Action failed');
        }
      } catch (error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**Error applying action:** ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: new Date().toISOString()
        }]);
      }
      setActionLoading(false);
    } else if (button.action === 'send_batch_questions' && button.data) {
      // Send batch questions to client
      setActionLoading(true);
      try {
        const result = await api.post(`/api/v1/assistant/actions/send-batch-questions?claim_id=${claimId}`, button.data.groups, { showErrorToast: false });
        if (result.success) {
          let msg = `**Batch Questions Sent!**\n\nCreated ${result.created_count} clarification requests:\n`;
          for (const req of result.requests || []) {
            msg += `- ${req.group_name} (${req.transaction_count} items)\n`;
          }
          msg += `\nMagic links have been generated. Send the email to notify the client.`;
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: msg,
            timestamp: new Date().toISOString()
          }]);
        } else {
          throw new Error(result.detail || 'Failed to send batch questions');
        }
      } catch (error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**Error sending batch questions:** ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: new Date().toISOString()
        }]);
      }
      setActionLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick question suggestions
  const quickQuestions = [
    { icon: AlertCircle, text: "Summarize anomalies", query: "Summarize the top anomalies and explain why they were flagged" },
    { icon: FileText, text: "What's missing?", query: "What information or documents are still missing from this claim?" },
    { icon: Zap, text: "Explain calculation", query: "Explain how the final claim amount was calculated" },
    { icon: MessageSquare, text: "Parse client responses", query: "Are there any client responses that need AI parsing for category suggestions?" },
  ];

  // Smart Review - One-click analysis
  const handleSmartReview = async () => {
    setSmartReviewLoading(true);
    try {
      const result = await api.post('/api/v1/assistant/actions/smart-review', {
        claim_id: claimId,
        include_anomalies: true,
        include_low_confidence: true,
        include_pending_clarifications: true,
        auto_categorize_threshold: 0.90
      }, { showErrorToast: false });
      
      setSmartReviewResult(result);
        
        // Build summary message
        let summary = `## Smart Review Results\n\n`;
        summary += `**${result.claimant_name}** - Health Score: ${result.health_score}/100\n\n`;
        summary += result.summary + '\n\n';
        
        if (result.counts) {
          summary += `### Items Found:\n`;
          if (result.counts.anomalies > 0) summary += `- ${result.counts.anomalies} anomalies\n`;
          if (result.counts.low_confidence > 0) summary += `- ${result.counts.low_confidence} low-confidence items\n`;
          if (result.counts.pending_clarifications > 0) summary += `- ${result.counts.pending_clarifications} pending clarifications\n`;
          if (result.counts.pending_ai_approvals > 0) summary += `- ${result.counts.pending_ai_approvals} AI suggestions awaiting approval\n`;
          if (result.counts.auto_applied > 0) summary += `- ${result.counts.auto_applied} high-confidence items auto-approved\n`;
        }
        
        if (result.recommended_actions && result.recommended_actions.length > 0) {
          summary += `\n### Recommended Actions:\n`;
          for (const action of result.recommended_actions.slice(0, 5)) {
            summary += `- ${action.description}\n`;
          }
        }
        
        // Build action buttons for recommended actions
        const actionButtons = result.recommended_actions?.slice(0, 3).map((action: any) => ({
          label: action.action_type === 'approve_ai' ? `Approve: ${action.suggested_category}` :
                 action.action_type === 'recategorize' ? `Recategorize: ${action.suggested_category}` :
                 action.action_type === 'dismiss' ? `Dismiss anomaly` : action.description.slice(0, 30),
          action: 'apply_smart_action',
          data: action
        })) || [];
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: summary,
          timestamp: new Date().toISOString(),
          actionButtons
        }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**Smart Review Error:** ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setSmartReviewLoading(false);
    }
  };

  // Batch Question Generation
  const handleBatchQuestions = async () => {
    setBatchQuestionsLoading(true);
    try {
      const result = await api.post('/api/v1/assistant/actions/generate-batch-questions', {
        claim_id: claimId,
        min_transactions_per_group: 2,
        max_groups: 5
      }, { showErrorToast: false });
      
      setBatchQuestionsResult(result);
      
      let summary = `## Batch Question Analysis\n\n`;
      summary += `Found **${result.total_transactions}** transactions needing review.\n`;
      summary += `Grouped into **${result.groups?.length || 0}** batches for consolidated questions.\n\n`;
      
      if (result.groups && result.groups.length > 0) {
        summary += `### Question Groups:\n`;
        for (const group of result.groups) {
          summary += `\n**${group.group_name}** (${group.transaction_count} items, ${formatCurrency(group.total_amount != null ? Math.abs(group.total_amount) : group.total_amount)})\n`;
          summary += `> ${group.consolidated_question}\n`;
          summary += `Suggested: ${group.suggested_category} (${Math.round((group.confidence || 0) * 100)}% confidence)\n`;
        }
      }
      
      if (result.ungrouped_transactions?.length > 0) {
        summary += `\n### Ungrouped (${result.ungrouped_transactions.length} items)\n`;
        summary += `These transactions are unique and should be asked individually.\n`;
      }
      
      // Add action button to send batch questions
      const actionButtons = result.groups?.length > 0 ? [{
        label: 'Send Batch Questions to Client',
        action: 'send_batch_questions',
        data: { groups: result.groups }
      }] : [];
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: summary,
        timestamp: new Date().toISOString(),
        actionButtons
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**Batch Questions Error:** ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setBatchQuestionsLoading(false);
    }
  };

  // Clear session and start fresh
  const handleClearSession = async () => {
    try {
      await api.delete(`/api/v1/assistant/history/${claimId}?session_id=${sessionId}`, { showErrorToast: false });
      setMessages([]);
      setIsInitialized(false);
      setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
      loadInitialSuggestions();
    } catch (error) {
      console.error('Failed to clear session:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div
        className={`
          fixed right-0 top-0 h-full w-full sm:w-[420px] shadow-2xl z-50
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col
        `}
        style={{ background: 'var(--color-bg-surface)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-base)' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
              <Bot className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div>
              <h2 className="cs-h3">QA Assistant</h2>
              <p className="cs-meta">Powered by Claude AI</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Session info toggle */}
            <button
              onClick={() => setShowSessionInfo(!showSessionInfo)}
              className="p-2 rounded-lg transition"
              style={
                showSessionInfo
                  ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
                  : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
              }
              title="Session info"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            {/* Voice toggle */}
            <button
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className="p-2 rounded-lg transition"
              style={
                voiceEnabled
                  ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
                  : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
              }
              title={voiceEnabled ? 'Voice enabled' : 'Voice disabled'}
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Session Info Bar - collapsible */}
        {showSessionInfo && (
          <div
            className="px-4 py-2 flex items-center justify-between"
            style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border)' }}
          >
            <div className="flex items-center gap-2 cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
              <MessageSquare className="w-4 h-4" />
              <span style={{ fontWeight: 500 }}>{messages.length} messages</span>
              <span style={{ color: 'var(--color-text-dim)' }}>•</span>
              <span className="cs-meta truncate max-w-[150px]" title={sessionId}>
                Session: {sessionId.slice(0, 15)}...
              </span>
            </div>
            <button
              onClick={handleClearSession}
              className="cs-meta px-2 py-1 rounded transition"
              style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
            >
              New Session
            </button>
          </div>
        )}

        {/* Stats Bar */}
        {(anomalyCount > 0 || reviewCount > 0) && (
          <div
            className="flex items-center gap-4 px-4 py-2"
            style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border)' }}
          >
            {anomalyCount > 0 && (
              <div className="flex items-center gap-1.5 cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                <AlertCircle className="w-4 h-4" />
                <span>{anomalyCount} anomalies</span>
              </div>
            )}
            {reviewCount > 0 && (
              <div className="flex items-center gap-1.5 cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                <FileText className="w-4 h-4" />
                <span>{reviewCount} need review</span>
              </div>
            )}
          </div>
        )}

        {/* Voice selector - show when voice is enabled */}
        {voiceEnabled && (
          <div
            className="px-4 py-2 flex items-center gap-2"
            style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border)' }}
          >
            <Volume2 className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
            <select
              value={selectedVoice}
              onChange={(e) => setSelectedVoice(e.target.value)}
              className="cs-meta bg-transparent border-none focus:outline-none cursor-pointer"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <option value="nova">Nova (Energetic)</option>
              <option value="alloy">Alloy (Neutral)</option>
              <option value="coral">Coral (Warm)</option>
              <option value="echo">Echo (Calm)</option>
              <option value="onyx">Onyx (Deep)</option>
              <option value="sage">Sage (Wise)</option>
              <option value="shimmer">Shimmer (Cheerful)</option>
            </select>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className="max-w-[85%] rounded-2xl px-4 py-3"
                style={
                  message.role === 'user'
                    ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)', borderBottomRightRadius: 6 }
                    : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderBottomLeftRadius: 6 }
                }
              >
                {message.role === 'assistant' ? (
                  <div className="prose prose-sm max-w-none cs-body">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        strong: ({ children }) => <strong style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{children}</strong>,
                        code: ({ children }) => <code className="cs-figure px-1 rounded" style={{ background: 'var(--color-bg-hover)' }}>{children}</code>,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="cs-body" style={{ color: 'var(--color-on-brand)' }}>{message.content}</p>
                )}

                {/* Play audio button for assistant messages */}
                {message.audioBase64 && message.role === 'assistant' && (
                  <button
                    onClick={() => isPlaying ? stopAudio() : playAudio(message.audioBase64!)}
                    className="mt-2 flex items-center gap-1 cs-meta"
                    style={{ color: 'var(--color-needs-human-text)' }}
                  >
                    {isPlaying ? <Square className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                    {isPlaying ? 'Stop' : 'Play audio'}
                  </button>
                )}

                {/* Action Buttons */}
                {message.actionButtons && message.actionButtons.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {message.actionButtons.map((button, btnIndex) => (
                      <button
                        key={btnIndex}
                        onClick={() => handleActionButton(button)}
                        className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded-full transition shadow-sm"
                        style={{ background: 'var(--color-bg-surface)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                      >
                        <ChevronRight className="w-3 h-3" />
                        {button.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-4 py-3" style={{ background: 'var(--color-bg-elevated)', borderBottomLeftRadius: 6 }}>
                <div className="flex items-center gap-2 cs-meta">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isRecording ? 'Processing voice...' : 'Thinking...'}</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions - Smart Review & Batch Questions */}
        {messages.length <= 2 && !isLoading && (
          <div className="px-4 pb-2">
            <p className="cs-label mb-2 flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> Power Actions:</p>
            <div className="flex gap-2 mb-3">
              <button
                onClick={handleSmartReview}
                disabled={smartReviewLoading}
                className="cs-chip flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg transition shadow-sm disabled:opacity-50"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {smartReviewLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Zap className="w-3 h-3" />
                )}
                Smart Review
              </button>
              <button
                onClick={handleBatchQuestions}
                disabled={batchQuestionsLoading}
                className="cs-chip flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg transition shadow-sm disabled:opacity-50"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }}
              >
                {batchQuestionsLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <FileText className="w-3 h-3" />
                )}
                Batch Questions
              </button>
            </div>
          </div>
        )}

        {/* Quick Questions */}
        {messages.length <= 1 && !isLoading && (
          <div className="px-4 pb-2">
            <p className="cs-meta mb-2">Quick questions:</p>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(q.query)}
                  className="cs-chip flex items-center gap-1.5 px-3 py-1.5 rounded-full transition"
                  style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
                >
                  <q.icon className="w-3 h-3" />
                  {q.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4" style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}>
          <div className="flex items-end gap-2">
            {/* Mic button */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isLoading}
              className="p-3 rounded-xl transition disabled:opacity-50"
              style={
                isRecording
                  ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                  : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
              }
              title={isRecording ? 'Stop recording' : 'Start voice input'}
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isRecording ? "Recording... click mic to stop" : "Ask about transactions, anomalies..."}
                rows={1}
                disabled={isRecording}
                className="cs-body w-full px-4 py-3 pr-12 rounded-xl resize-none focus:outline-none"
                style={{
                  maxHeight: '120px',
                  background: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || isLoading || isRecording}
                className="absolute right-2 bottom-2 p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <p className="cs-meta mt-2 text-center" style={{ color: 'var(--color-text-dim)' }}>
            <Sparkles className="w-3 h-3 inline mr-1" />
            {isRecording ? 'Speak clearly, then click the mic to stop' : 'Type or tap mic to speak'}
          </p>
        </div>
      </div>
    </>
  );
}
